import clsx from 'clsx'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ContentBlock from '@/components-latest/primitives/ContentBlock'
import Text from '@/components-latest/primitives/Text'
import ReviewAverage from '@/components-latest/ui/PerformancePage/ReviewAverage'
import ReviewsModal, {
  IReviewsModalProps
} from '@/components-latest/ui/PerformancePage/ReviewsModal'
import Store from '@/components-latest/primitives/Icons/Store'

import Styles from './ReviewsPerformance.module.scss'
import LoadingSpinner from '@/components-latest/primitives/Spinner/LoadingSpinner'

interface BranchReviewData {
  label: string
  data: {
    avg_client_rating: number
    avg_driver_rating: number
    client_rating_details: any[]
    driver_rating_details: any[]
  }
}

interface IReviewsPerformanceProps {
  mode: 'single' | 'compare'
  branches: BranchReviewData[]
  isLoading?: boolean
  error?: string | null
}

const ReviewsPerformance: React.FC<IReviewsPerformanceProps> = ({
  mode,
  branches,
  isLoading = false,
  error = null
}) => {
  const { t } = useTranslation()
  const [modalProps, setModalProps] = useState<
    Omit<IReviewsModalProps, 'onOpenChange'> & { branchLabel?: string }
  >({
    reviews: [],
    title: '',
    userPrefix: 'client',
    open: false,
    branchLabel: ''
  })

  const handleOpenChange = useCallback(
    (isOpen: boolean) => setModalProps(state => ({ ...state, open: isOpen })),
    []
  )

  return (
    <ContentBlock className={Styles.contentBlock}>
      {/* Branch label */}
      {mode === 'single' && branches[0]?.label && (
        <div className={clsx(Styles.singleTitle, Styles.topBranchLabel)}>
          <div className={Styles.icon}>
            <Store />
          </div>
          <Text variant="title-1" className={Styles.textlabel}>
            {branches[0].label}
          </Text>
        </div>
      )}
      {/* Section title */}
      <Text variant="nextTitle-2" weight="semiBold" className={Styles.sectionTitle} asChild>
        <h2>{t('performance.reviews.title')}</h2>
      </Text>
      {isLoading ? (
        <div className={Styles.spinnerWrapper}>
          <LoadingSpinner 
            containerType="centered" 
            text="Loading reviews data..."
          />
        </div>
      ) : error ? (
        <div className={Styles.spinnerWrapper}>
          <Text variant="subtitle" color="error">{error}</Text>
        </div>
      ) : mode === 'compare' && branches.length > 1 ? (
        <div className={Styles.reviewsTableWrapper}>
          <table className={Styles.reviewsTable}>
            <thead>
              <tr className={Styles.reviewsTableHeaderRow}>
                <th className={Styles.branchHeaderCell}></th>
                <th className={Styles.reviewsTableHeaderCell}>{t('performance.reviews.client-reviews')}</th>
                <th className={Styles.reviewsTableHeaderCell}>{t('performance.reviews.driver-reviews')}</th>
              </tr>
            </thead>
            <tbody>
              {branches.map((branch, idx) => (
                <tr key={idx} className={Styles.reviewsTableRow}>
                  <td className={Styles.branchCell}>
                    <div className={Styles.branchLabelCell}>
                      <div className={Styles.icon}><Store /></div>
                      <Text variant="subtitle" className={Styles.branchLabelText}>{branch.label}</Text>
                    </div>
                  </td>
                  <td className={Styles.reviewsTableCell}>
                    <ReviewAverage
                      label={t('performance.reviews.client-reviews')}
                      value={branch.data.avg_client_rating}
                    />
                  </td>
                  <td className={Styles.reviewsTableCell}>
                    <ReviewAverage
                      label={t('performance.reviews.driver-reviews')}
                      value={branch.data.avg_driver_rating}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        branches.map((branch, index) => (
          <React.Fragment key={index}>
            {/* <div className={clsx(Styles.singleTitle, { [Styles.noTitle]: !branch.label })}>
              {branch.label && (
                <div className={Styles.icon}>
                  <Store />
                </div>
              )}
              <Text variant="title-1" className={Styles.textlabel}>
                {branch.label}
              </Text>
            </div> */}
            <div className={Styles.reviewsCard}>
              <div className={Styles.reviewsCardHeader}>
                <span className={Styles.reviewColumnTitleLeft}>{t('performance.reviews.client-reviews')}</span>
                <span className={Styles.reviewColumnTitleRight}>{t('performance.reviews.driver-reviews')}</span>
              </div>
              <div className={Styles.reviewColumnsRow}>
                <div className={Styles.reviewColumn}>
                  <ReviewAverage
                    label={t('performance.reviews.client-reviews')}
                    value={branch.data.avg_client_rating}
                  />
                </div>
                <div className={`${Styles.reviewColumn} ${Styles.reviewColumnRight}`}>
                  <ReviewAverage
                    label={t('performance.reviews.driver-reviews')}
                    value={branch.data.avg_driver_rating}
                  />
                </div>
              </div>
            </div>
          </React.Fragment>
        ))
      )}
    </ContentBlock>
  )
}

export default ReviewsPerformance
